import { useState, useEffect } from "react";
import { db } from "@/lib/firebase";
import { collection, getDocs, query, orderBy, doc, updateDoc } from "firebase/firestore";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Receipt,
  Wrench,
  Search,
  Loader2,
  TrendingUp,
  ClipboardList,
  CheckCircle2,
  BarChart3,
  Calendar,
} from "lucide-react";

const formatDate = (d) => {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return d; }
};

const formatCurrency = (v) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(v || 0);

const typeLabel = (t) =>
  t === "sales_invoice" ? "Sales Invoice" : t === "service_bill" ? "Service Bill" : t;

const typeBadgeClass = (t) =>
  t === "sales_invoice"
    ? "bg-primary/10 text-primary border-primary/30"
    : "bg-orange-500/10 text-orange-600 border-orange-500/30";

const AccountingPanel = ({ onSignOut }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [updatingId, setUpdatingId] = useState(null);

  const fetchEntries = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(query(collection(db, "pending_entries"), orderBy("createdAt", "desc")));
      setEntries(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch {
      toast.error("Failed to load entries. Check Firebase configuration.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchEntries(); }, []);

  const handleMarkReviewed = async (id) => {
    setUpdatingId(id);
    try {
      await updateDoc(doc(db, "pending_entries", id), { status: "reviewed" });
      setEntries((prev) => prev.map((e) => e.id === id ? { ...e, status: "reviewed" } : e));
      toast.success("Marked as reviewed.");
    } catch {
      toast.error("Failed to update. Try again.");
    } finally {
      setUpdatingId(null);
    }
  };

  const filtered = entries.filter((e) => {
    const term = search.toLowerCase();
    return (
      (e.customerName || "").toLowerCase().includes(term) ||
      (e.invoiceNumber || e.billNumber || "").toLowerCase().includes(term)
    );
  });

  const pending = entries.filter((e) => e.status === "pending");
  const reviewed = entries.filter((e) => e.status === "reviewed");
  const totalAmount = entries.reduce((s, e) => s + (e.amount || 0), 0);

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-20 bg-card/95 backdrop-blur-sm border-b border-border px-4 lg:px-6 h-16 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-blue-500/10">
            <BarChart3 className="h-5 w-5 text-blue-600" />
          </div>
          <div>
            <h1 className="font-heading font-semibold text-lg text-foreground">Accounting Panel</h1>
          </div>
          <Badge variant="outline" className="hidden sm:inline-flex text-xs border-blue-500/40 text-blue-600 bg-blue-500/5">
            SHREE MOBILITY TRADE
          </Badge>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={onSignOut}
          className="gap-2 text-destructive border-destructive/30 hover:bg-destructive/10 hover:border-destructive/60"
        >
          Sign Out
        </Button>
      </header>

      <main className="p-4 lg:p-6 space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-5 pb-5 flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Entries</p>
                <p className="text-3xl font-bold text-foreground mt-1">{entries.length}</p>
              </div>
              <div className="p-3 rounded-lg bg-secondary">
                <ClipboardList className="h-5 w-5 text-muted-foreground" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-5 pb-5 flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Pending Review</p>
                <p className="text-3xl font-bold text-orange-500 mt-1">{pending.length}</p>
              </div>
              <div className="p-3 rounded-lg bg-orange-500/10">
                <Calendar className="h-5 w-5 text-orange-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-5 pb-5 flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Value</p>
                <p className="text-2xl font-bold text-primary mt-1">{formatCurrency(totalAmount)}</p>
              </div>
              <div className="p-3 rounded-lg bg-primary/10">
                <TrendingUp className="h-5 w-5 text-primary" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Pending Entries Table */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <CardTitle className="font-heading text-base">Pending Entries from Dealership</CardTitle>
              <Button variant="outline" size="sm" onClick={fetchEntries} className="gap-2 text-xs">
                Refresh
              </Button>
            </div>
            <div className="relative max-w-sm mt-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by customer or document..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="h-7 w-7 animate-spin text-primary" />
                <span className="ml-3 text-muted-foreground">Loading entries...</span>
              </div>
            ) : filtered.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <CheckCircle2 className="h-12 w-12 text-muted-foreground/30 mb-3" />
                <p className="text-muted-foreground font-medium">
                  {search ? "No matching entries" : "No entries found"}
                </p>
                <p className="text-sm text-muted-foreground/60 mt-1">
                  Entries appear here when dealership saves invoices or service bills
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto rounded-lg border border-border">
                <table className="w-full">
                  <thead>
                    <tr className="bg-secondary/50">
                      {["Type", "Document No.", "Date", "Customer", "Amount", "Status", "Action"].map((h) => (
                        <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide whitespace-nowrap">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {filtered.map((entry) => (
                      <tr key={entry.id} className="hover:bg-secondary/30 transition-colors">
                        <td className="px-4 py-3">
                          <Badge variant="outline" className={`text-xs ${typeBadgeClass(entry.type)}`}>
                            {entry.type === "sales_invoice" ? (
                              <Receipt className="h-3 w-3 mr-1" />
                            ) : (
                              <Wrench className="h-3 w-3 mr-1" />
                            )}
                            {typeLabel(entry.type)}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-sm font-medium text-foreground">
                          {entry.invoiceNumber || entry.billNumber || "—"}
                        </td>
                        <td className="px-4 py-3 text-sm text-muted-foreground whitespace-nowrap">
                          {formatDate(entry.date)}
                        </td>
                        <td className="px-4 py-3 text-sm text-foreground">{entry.customerName || "—"}</td>
                        <td className="px-4 py-3 text-sm font-semibold text-primary whitespace-nowrap">
                          {formatCurrency(entry.amount)}
                        </td>
                        <td className="px-4 py-3">
                          <Badge
                            variant={entry.status === "reviewed" ? "secondary" : "outline"}
                            className={`text-xs capitalize ${
                              entry.status === "reviewed"
                                ? "bg-green-500/10 text-green-700 border-green-500/30"
                                : "bg-orange-500/10 text-orange-600 border-orange-500/30"
                            }`}
                          >
                            {entry.status || "pending"}
                          </Badge>
                        </td>
                        <td className="px-4 py-3">
                          {entry.status !== "reviewed" && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleMarkReviewed(entry.id)}
                              disabled={updatingId === entry.id}
                              className="gap-1.5 h-7 text-xs text-green-700 border-green-500/30 hover:bg-green-500/10"
                            >
                              {updatingId === entry.id ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                              ) : (
                                <CheckCircle2 className="h-3 w-3" />
                              )}
                              Reviewed
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Info note */}
        <p className="text-xs text-muted-foreground text-center">
          Full accounting features (ledger, reports, P&amp;L) — Phase 2 development
        </p>
      </main>
    </div>
  );
};

export default AccountingPanel;
